import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHubStore } from '../../store/useHubStore';
import { useOrgStore } from '../../store/useOrgStore';
import { FindingRow } from '../../components/shared/FindingRow';
import { severityRank } from '../../components/shared/SeverityBar';
import { HubPageHeader, HubSectionTitle } from '../../components/hub/HubPageHeader';
import { recordBaloClick, useBaloUrl } from '../../lib/hubApi';

export function HubOverviewPage() {
  const navigate = useNavigate();
  const { findings, loading, loadFindings, loadedForOrgId } = useHubStore();
  const audit = useOrgStore((s) => s.audit);
  // orgUrl is the instance URL — the stable identity for "which org are we
  // connected to". Refetch findings whenever this changes so org switches
  // never show stale data from a previous session.
  const orgUrl = useOrgStore((s) => s.orgUrl);
  const baloUrl = useBaloUrl();

  // Real paths from HealthScore type in org.types.ts:
  //   healthscore.total           — 0-100 org health
  //   healthscore.foundations.score — 0-15 foundations sub-score (null when ineligible)
  //   healthscore.dataHygiene.score — 0-100 data hygiene sub-score
  const totalScore = audit?.healthscore?.total ?? null;
  const foundationsValue = audit?.healthscore?.foundations?.score ?? null;
  const dataHygieneValue = audit?.healthscore?.dataHygiene?.score ?? null;

  useEffect(() => {
    if (orgUrl && orgUrl !== loadedForOrgId) {
      void loadFindings(orgUrl);
    }
  }, [orgUrl, loadedForOrgId, loadFindings]);

  const top = useMemo(
    () =>
      [...findings]
        .filter((f) => f.status === 'open' || f.status === 'bpx_in_progress')
        .sort((a, b) => severityRank(a.severity) - severityRank(b.severity))
        .slice(0, 5),
    [findings],
  );

  return (
    <section className="max-w-4xl space-y-10">
      <HubPageHeader
        eyebrow="What needs your attention"
        title="Your Salesforce, today."
      />

      <div className="space-y-3">
        <HubSectionTitle>Top priorities</HubSectionTitle>
        {loading && top.length === 0 ? (
          <p className="text-sm text-text-muted">Loading findings…</p>
        ) : top.length === 0 ? (
          <div className="rounded-lg border border-border-subtle bg-surface-2/40 px-4 py-5 text-sm text-text-muted">
            No active findings. Your org is on a healthy trajectory.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {top.map((f) => (
              <FindingRow
                key={f.id}
                id={f.id}
                severity={f.severity}
                category={f.category}
                title={f.title}
                context={f.context}
                status={f.status}
                caseRef={f.caseRef ?? undefined}
                onClick={() => navigate(`/hub/findings#${f.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {baloUrl ? (
        <div className="rounded-lg border border-border-subtle bg-surface-2 px-5 py-4 flex items-center gap-4 shadow-card">
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-text-strong">Talk it through with a Salesforce expert</div>
            <p className="text-sm text-text-muted mt-0.5">
              Book a call with Cyril Duvignau on Balo to plan the fixes for these findings.
            </p>
          </div>
          <a
            href={baloUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordBaloClick('/api/hub/balo-click')}
            className="shrink-0 text-sm px-4 py-2 rounded-md bg-sfblue text-white font-semibold hover:opacity-90 transition-opacity"
          >
            Book a call
          </a>
        </div>
      ) : null}

      <div className="space-y-3">
        <HubSectionTitle>Sub-scores</HubSectionTitle>
        <div className="grid grid-cols-3 gap-3">
          <ScoreCard label="Org Health" value={totalScore} max={100} />
          <ScoreCard label="Foundations" value={foundationsValue} max={15} />
          <ScoreCard label="Data Hygiene" value={dataHygieneValue} max={100} />
        </div>
      </div>
    </section>
  );
}

// Score-to-band mapping. We normalize against max so the 0–15 Foundations
// score uses the same thresholds as 0–100 once expressed as a percentage.
function scoreBand(value: number, max: number): 'success' | 'warning' | 'danger' {
  const pct = (value / max) * 100;
  if (pct >= 80) return 'success';
  if (pct >= 50) return 'warning';
  return 'danger';
}

function ScoreCard({
  label,
  value,
  max,
}: {
  label: string;
  value: number | null;
  max: number;
}) {
  const band = value !== null ? scoreBand(value, max) : null;
  const valueColor =
    band === 'success' ? 'text-success' :
    band === 'warning' ? 'text-warning' :
    band === 'danger'  ? 'text-danger'  : 'text-text';
  return (
    <div className="group rounded-lg border border-border-subtle bg-surface-2 px-4 py-5 shadow-card transition-all hover:bg-surface-hover hover:shadow-card-hover hover:border-border-strong">
      <div className="flex items-baseline gap-1">
        <span className={`text-[32px] leading-none font-semibold tabular-nums ${valueColor}`}>
          {value ?? '-'}
        </span>
        <span className="text-xs text-text-muted">/ {max}</span>
      </div>
      <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
        {label}
      </div>
    </div>
  );
}
